import CloseIcon from '@mui/icons-material/Close';
import { Grid } from '@mui/material';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { DIGITAL_RETAILING_MODAL_CLOSE, errorMessage } from 'constants/CommonConstants';
import { PHONE_REGEX } from 'constants/Regex';
import dayjs from 'dayjs';
import { formPatternRules, formRequiredRules } from 'helper';
import React, { useEffect, useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { CommonInput } from 'sharedComponents/inputGroup';
import { AutoCompleteAddress } from 'sharedComponents/inputGroup/AutoCompleteAddress';
import { CommonPhoneInput } from 'sharedComponents/inputGroup/CommonPhoneInput';
import { DateCalendarInput } from 'sharedComponents/inputGroup/DateCalendar';
import { InputRadio } from 'sharedComponents/inputGroup/InputRadio';
import { RootState } from 'store/store';
import { clearLeadCreation } from 'store/TradeInState';
import { MODULE_NAME, TIME_OPTIONS } from '../DigitalRetailingConstant';
import testDriveImg from '../icons/testdrive-img.png';

const Step13: React.ForwardRefRenderFunction<unknown, StepperFromProps> = ({ onBack }, ref) => {
  // Hooks
  const { t } = useTranslation();

  const today = dayjs();

  const { control, trigger, setValue, getValues } = useFormContext();

  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  useEffect(() => {
    const { firstName, lastName, email, phone } = getValues();
    setValue('calendar_firstname', firstName);
    setValue('calendar_lastname', lastName);
    setValue('calendar_emailaddress', email);
    setValue('calendar_phone', phone);
  }, []);

  const backStep = () => {
    onBack?.(7);
  };

  useImperativeHandle(ref, () => ({
    backStep,
  }));

  const onCloseModal = () => {
    window.parent.postMessage(
      { type: DIGITAL_RETAILING_MODAL_CLOSE, data: { close: true }, res: { module: 'digital-retailing' } },
      '*'
    );
    dispatch(clearLeadCreation());
    localStorage.setItem(`${MODULE_NAME}-currentStep`, '0');
    localStorage.removeItem(MODULE_NAME);
  };

  const timeOptions = TIME_OPTIONS.map((time) => ({ label: time, value: time }));

  return (
    <>
      <div className='dr-widget-steps'>
        <div className='dr-widget-steps__content position-relative'>
          <button type='button' className='btn-close' onClick={onCloseModal}>
            <CloseIcon />
          </button>
          <div className='dr-widget-steps__content-header mb-4'>
            <div className='dr-widget-steps__content-header__left'>
              <h2 className='dr-widget-steps__title'>{t('digitalRetailing.bookTestDrive')}</h2>
              <p className='dr-widget-steps__step-note text-secondary mb-0'>
                {t('digitalRetailing.experiencePerformance')}
              </p>
            </div>
            <div className='dr-widget-steps__content-header__right'>
              <img src={testDriveImg} alt='Test' />
            </div>
          </div>
          <div className='dr-widget-steps__form-wrapper'>
            <h3 className='form-section-title'>{t('digitalRetailing.calendarTime')}</h3>
            <Grid container spacing={2} marginBottom={3}>
              <Grid item lg={4} md={5} sm={12} xs={12}>
                <div className='dr-widget-steps__calendar'>
                  <DateCalendarInput
                    name='calendar_appointmentDate'
                    control={control}
                    rules={formRequiredRules(errorMessage(t).dobRequired)}
                    triggerValidationForField={trigger}
                    minDate={today}
                  />
                </div>
              </Grid>
              <Grid item lg={8} md={7} sm={12} xs={12}>
                <div className='dr-widget-steps__time'>
                  <InputRadio
                    name={'calendar_time'}
                    control={control}
                    options={timeOptions}
                    triggerValidationForField={trigger}
                    rules={formRequiredRules(errorMessage(t).radioError)}
                  />
                </div>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item md={6} sm={6} xs={12}>
                <CommonInput
                  name='calendar_firstname'
                  control={control}
                  label={t('stepperForm.firstName')}
                  placeholder={t('stepperForm.enterFirstName')}
                  triggerValidationForField={trigger}
                  rules={formRequiredRules(errorMessage(t).required)}
                />
              </Grid>
              <Grid item md={6} sm={6} xs={12}>
                <CommonInput
                  name='calendar_lastname'
                  control={control}
                  label={t('stepperForm.lastName')}
                  placeholder={t('stepperForm.enterLastName')}
                  triggerValidationForField={trigger}
                  rules={formRequiredRules(errorMessage(t).required)}
                />
              </Grid>
              <Grid item md={6} sm={6} xs={12}>
                <CommonInput
                  name='calendar_emailaddress'
                  control={control}
                  label={t('stepperForm.emailEg')}
                  placeholder={t('stepperForm.enterEmail')}
                  triggerValidationForField={trigger}
                  rules={formRequiredRules(errorMessage(t).required)}
                />
              </Grid>
              <Grid item md={6} sm={6} xs={12}>
                <CommonPhoneInput
                  name='calendar_phone'
                  control={control}
                  label={t('stepperForm.phone')}
                  placeholder={t('stepperForm.enterPhone')}
                  triggerValidationForField={trigger}
                  rules={{
                    ...formRequiredRules(errorMessage(t).phoneRequired),
                    ...formPatternRules(PHONE_REGEX, errorMessage(t).invalidPhoneFormat),
                  }}
                />
              </Grid>
              <Grid item md={12}>
                <AutoCompleteAddress
                  name='calendar_address'
                  control={control}
                  setValue={setValue}
                  label={t('stepperForm.address')}
                  placeholder={t('stepperForm.enterAddress')}
                  rules={formRequiredRules(errorMessage(t).required)}
                  triggerValidationForField={trigger}
                />
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
    </>
  );
};

const StepperForm13 = React.forwardRef(Step13);

export default StepperForm13;
